import React, { useState } from 'react'
import styled from 'styled-components'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import favicon from '../images/favicon.ico'
import Button from '../components/Button'
import Container from '../Layout/Container'
import CTA from '../sections/CTA'
import Estimation from '../components/Estimation'
import ExtendedPageHeader from '../sections/ExtendedPageHeaderSection'
import FAQ from '../sections/FAQ'
import FormSection from '../sections/Form'
import GeneralSection from '../sections/GeneralSection'
import Layout from '../Layout/Layout'
import MessagesContext from '../context/MessagesContext'
import PlanList from '../components/PlanList'
import PricingPageForm from '../components/PricingPageForm'
import SEO from '../components/seo'
import TextHeader from '../components/TextHeader'

const StyledWrapper = styled.div`
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  width: calc(100vw - 40px);

  ${({ theme }) => theme.media.desktop} {
    flex-wrap: nowrap;
    max-width: 1240px;
  }
`

const EnterpriseContainer = styled.div`
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
    0 0 1px 0 rgba(10, 22, 70, 0.06);
  margin-bottom: 88px;
  padding: 48px 32px;
  text-align: center;
  width: 100%;
  z-index: 1;

  h2 {
    font-size: 1.75em;
    font-weight: 300;
    margin-bottom: 16px;
  }

  p {
    color: #4e5d78;
    font-size: 14px;

    :first-of-type {
      margin-bottom: 12px;
    }

    :last-of-type {
      margin-bottom: 24px;
    }
  }
`

const StyledForm = styled.div`
  grid-area: 2/1/2/2;
  margin: 0 auto;
  margin-bottom: 120px;
  margin-top: -40px;
  
  ${({ theme }) => theme.media.desktop} {
    grid-area: 1/2/2/4;
    max-width: 80%;
  }
`

const FormSectionData = {
  centered: true,
  fullWidth: true,
  sectionType: 'Form',
  items: [
    {
      fields: {
        themeSize: 'tiny',
        themeAlign: 'center',
      },
    },
    {
      fields: {
        formType: 'pricing-page-form'
      }
    },
  ],
}

const DEFAULT_MESSAGES = 10000;
const MSG_BREAKPOINT = 3000000;

const PricingPage = ({ data }) => {
  const {
    faqHeader,
    headerTextHeader,
    tabs,
    troubleTextHeader,
  } = data.allContentfulPricingPage.edges[0].node

  const { subtitleTop, title } = headerTextHeader
  const troubleTextHeaderButton = troubleTextHeader.filter(
    item => item.__typename === 'ContentfulButton'
  )[0]

  const [recommendedPlan, setRecommendedPlan] = useState({})
  const [messages, setMessages] = useState(DEFAULT_MESSAGES)
  const messageContextValue = { messages, setMessages }
  const swapOrder = messages >= MSG_BREAKPOINT;

  return (
    <Layout>
      <SEO
        title="Pricing"
        url="https://www.mailgun.com/pricing/"
        type="website"
      />
      <Helmet defer={false}>
        <title>Flexible Pricing & Email Delivery Plans - Email API Service</title>
        <meta
          name="description"
          content="Pricing plans built for an email delivery solution that brings emails and validations together under one API-driven engine."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.mailgun.com/pricing/" />
        <link rel="icon" href={favicon} />
      </Helmet>
      <Container fullWidth>
        <MessagesContext.Provider value={messageContextValue}>
          <ExtendedPageHeader>
            <header>
              {/* The main section is on the dark blue background */}
              <TextHeader
                mdTitle={title.title}
                subtitle={subtitleTop}
                theme={{ size: 'big', align: 'center' }}
              />
              <Estimation
                setRecommendedPlan={setRecommendedPlan}
                plans={tabs[0].plans}
                recommendedPlan={recommendedPlan}
              />
            </header>

            <main>
              <StyledWrapper>
                {swapOrder &&
                  <StyledForm>
                    <FormSection
                      blogListing={true}
                      noShift={true}
                      cmsData={FormSectionData}
                    >
                      <TextHeader
                        title={<p className="pp-form-title">Deliverability Services for Enterprise Email</p>}
                        text="The full service for high volume senders"
                        theme={{
                          size: 'tiny',
                          align: 'center',
                          width: 'wide',
                        }}
                      />
                      <PricingPageForm />
                    </FormSection>
                  </StyledForm>
                }

                <PlanList plans={tabs[0].plans} recommended={recommendedPlan} />

                <EnterpriseContainer>
                  <h2>Enterprise Services</h2>
                  <p>
                    Partner with Mailgun to create an enterprise-grade solution
                    for delivering email at scale.
                  </p>
                  <p>
                    Includes everything in the Scale plan plus custom features,
                    Deliverability Services, and Rapid Fire Burst
                    Sending SLA.
                  </p>
                  <Button
                    primary="true"
                    cta="true"
                    external="true"
                    isAbsolutePath="true"
                    to="https://www.mailgun.com/enterprise/"
                  >
                    Contact Us
                  </Button>
                </EnterpriseContainer>
              </StyledWrapper>
            </main>
          </ExtendedPageHeader>
        </MessagesContext.Provider>
      </Container>
      <Container>
        <GeneralSection centered="true">
          <TextHeader
            mdTitle={faqHeader.title.title}
            subtitle={faqHeader.subtitleTop}
            theme={{
              size: 'small',
              align: 'center',
              width: 'wide',
            }}
          />
          <FAQ data={tabs[0].faq} />
          <TextHeader
            mdTitle={troubleTextHeader[0].title.title}
            subtitle={troubleTextHeader[0].subtitleTop}
            text={troubleTextHeader[0].text.text}
            theme={{
              size: 'small',
              align: 'center',
            }}
          />
          <Button
            ghost="true"
            to={troubleTextHeaderButton.buttonLink}
            external={troubleTextHeaderButton.external}
            isAbsolutePath={troubleTextHeaderButton.isAbsolutePath}
          >
            {troubleTextHeaderButton.buttonText}
          </Button>
        </GeneralSection>
      </Container>
      <Container fullWidth>
        <CTA ghost slick>
          <TextHeader
            title={
              <div className="title">
                It's easy to get started. <span>Let's get sending.</span>
              </div>
            }
            subtitle="sign up"
            text="See what you can accomplish with the world's best email delivery platform."
            theme={{
              size: 'big',
              align: 'center',
            }}
          />
          <Button
            primary="true"
            xxl="true"
            to="https://signup.mailgun.com/"
            external
            isAbsolutePath
          >
            Sign up for Free
          </Button>
        </CTA>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query PricingPageQuery {
    allContentfulPricingPage(filter: { name: { eq: "Pricing (PROD)" } }) {
      edges {
        node {
          headerTextHeader {
            title {
              title
            }
            subtitleTop
          }
          estimateHeaderTextHeader {
            title {
              title
            }
            subtitleTop
          }
          faqHeader {
            title {
              title
            }
            subtitleTop
          }
          troubleTextHeader {
            ... on ContentfulTextHeader {
              id
              subtitleTop
              text {
                text
              }
              title {
                title
              }
            }
            ... on ContentfulButton {
              id
              name
              buttonText
              buttonLink
              external
              isAbsolutePath
            }
          }
          tabs {
            ... on ContentfulPricingTab {
              id
              name
              shortName
              shortSubtitle
              headerTextHeader {
                ... on ContentfulTextHeader {
                  id
                  subtitleTop
                  text {
                    text
                  }
                  title {
                    title
                  }
                }
              }
              plans {
                ... on ContentfulPricingPlan {
                  id
                  name
                  tier
                  type
                  subtitle
                  icon {
                    file {
                      url
                    }
                  }
                  price
                  activeFeatures {
                    ... on ContentfulPricingFeature {
                      id
                      name
                      tooltip
                    }
                  }
                  disabledFeatures {
                    ... on ContentfulPricingFeature {
                      id
                      name
                    }
                  }
                  options {
                    ... on ContentfulPricingPlanOption {
                      id
                      name
                      price
                      messages
                      signUpLink
                      additionalMessage {
                        ... on ContentfulPricingPlanAdditional {
                          id
                          text
                          amount
                          price
                        }
                      }
                    }
                  }
                  additionalValidation {
                    ... on ContentfulPricingPlanAdditional {
                      id
                      text
                      amountText
                      price
                    }
                  }
                  button {
                    ... on ContentfulButton {
                      id
                      name
                      buttonText
                      theme
                    }
                  }
                }
              }
              faq {
                ... on ContentfulFaqQuestion {
                  id
                  question
                  answer {
                    answer
                  }
                }
              }
            }
          }
          signUpCtaTextHeader {
            ... on ContentfulTextHeader {
              id
              subtitleTop
              text {
                text
              }
              title {
                title
              }
            }
            ... on ContentfulButton {
              id
              name
              buttonText
              buttonLink
              external
              isAbsolutePath
            }
          }
        }
      }
    }
  }
`

export default PricingPage